import { baseApi } from "@/store/baseApi";

import { AddToAutoOrderProps, AutoOrderFrequencyLevels } from "@/app/ec/product/callToAction";
import fixImageURI from "@/app/ec/product/fixImageURI";

/** Modern users-service frequency enum (the wire value POST /users/subscriptions expects). */
export type SubscriptionFrequencyEnum = "BI_WEEKLY" | "MONTHLY" | "BI_MONTHLY";

/** Status discriminator on the modern subscribe response body. */
export type SubscribeStatus = "CREATED" | "ALREADY_SUBSCRIBED" | "NEEDS_INFO";

/**
 * Response body of POST /users/subscriptions (SubscribeResponse, @JsonInclude NON_NULL).
 * Only the success (201) outcome resolves through RTK Query's `.unwrap()`; the
 * 409 (ALREADY_SUBSCRIBED) and 422 (NEEDS_INFO) outcomes reject and arrive as a
 * `FetchBaseQueryError` whose `data` is this same shape — see `classifySubscribeError`.
 */
export interface SubscribeResponse {
  status: SubscribeStatus;
  // CREATED only:
  subscriptionId?: number;
  subscriptionProductId?: number;
  frequency?: SubscriptionFrequencyEnum;
  nextOrderDate?: string;
  futureOrderDates?: string[];
  // NEEDS_INFO only:
  missingShipping?: boolean;
  missingPayment?: boolean;
  missingLicense?: boolean;
}

/** The recurring frequency slugs a subscribe accepts — `one-time` is add-to-cart, not a subscribe. */
export type RecurringFrequencyLevel = Exclude<AutoOrderFrequencyLevels, "one-time">;

/**
 * FE frequency slug → modern users-service enum. Keyed by {@link RecurringFrequencyLevel}
 * (the three recurring slugs); `one-time` is intentionally not a subscribe target. The map is
 * total over the recurring slugs and the mutation arg below is narrowed to them, so the lookup
 * is provably non-undefined and an invalid request can't be constructed at compile time.
 */
const SUBSCRIBE_FREQUENCY_BY_SLUG: Record<RecurringFrequencyLevel, SubscriptionFrequencyEnum> = {
  "weeks-2": "BI_WEEKLY",
  "months-1": "MONTHLY",
  "months-2": "BI_MONTHLY",
};

/** subscribeToProduct payload: {@link AddToAutoOrderProps} with the frequency narrowed to recurring-only. */
export type SubscribeToProductArgs = Omit<AddToAutoOrderProps, "frequencyId"> & {
  frequencyId: RecurringFrequencyLevel;
};

export const orderApi = baseApi
  // subscribeToProduct invalidates the Auto Order caches; declare the tags so this
  // slice's builder knows them (autoOrderApi registers the same tags independently —
  // addTagTypes is additive on the shared baseApi).
  .enhanceEndpoints({ addTagTypes: ["AUTO_ORDER_SUBSCRIPTIONS", "AUTO_ORDER_POPULAR"] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getLastActiveProducts: builder.query<BuyItAgainSuggestions[], void>({
        query: () => "neo/order/user-last-active-products",
        transformResponse: (response: BuyItAgainSuggestions[]) => {
          return response.map((bia) => {
            return {
              ...bia,
              mediaPath: fixImageURI(bia.mediaPath),
              smallPhotoPath: fixImageURI(bia.smallPhotoPath),
            };
          });
        },
      }),
      /**
       * Subscribe a product to Auto Order via the modern users service
       * (POST /users/subscriptions, PI-1372). Outcome is carried by HTTP status:
       * 201 resolves with a CREATED {@link SubscribeResponse}; 409 (already
       * subscribed) / 422 (needs shipping / payment / license info) / 401 / 400 /
       * 500 reject and land in the caller's catch — branch via `classifySubscribeError`.
       *
       * The request body drops the legacy `SubscriberId` (ugrId comes from the
       * n32JWT cookie) and `LicenseRequired` — the license requirement is resolved
       * server-side from the catalog, not asserted by the client; the 422 NEEDS_INFO
       * response reports `missingLicense`. The FE frequency slug is mapped to the
       * users-service enum.
       */
      subscribeToProduct: builder.mutation<SubscribeResponse, SubscribeToProductArgs>({
        query: (productBeingSubscribed) => ({
          url: "users/subscriptions",
          credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: {
            mpId: productBeingSubscribed.mpId,
            frequency: SUBSCRIBE_FREQUENCY_BY_SLUG[productBeingSubscribed.frequencyId],
            quantity: productBeingSubscribed.quantity,
          },
        }),
        invalidatesTags: ["AUTO_ORDER_SUBSCRIPTIONS", "AUTO_ORDER_POPULAR"],
      }),
      submitOrderReturn: builder.mutation<OrderReturn.ReturnSubmissionResponse, OrderReturn.ReturnSubmissionRequest>({
        query: (returnRequest) => ({
          url: "next/order-return/submit",
          credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: returnRequest,
        }),
      }),
      submitOrderHelpRequest: builder.mutation<OrderHelp.HelpRequestResponse, OrderHelp.HelpRequestSubmission>({
        query: (helpRequest) => ({
          // =============================================================================
          // TODO: Replace with actual endpoint once backend is implemented
          // This endpoint should handle creating Zendesk tickets with file uploads
          // =============================================================================
          url: "next/order-help/submit",
          credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: helpRequest,
        }),
        // =============================================================================
        // STUB: Transform response to always succeed during development
        // Remove this once the backend endpoint is implemented
        // =============================================================================
        transformResponse: (): OrderHelp.HelpRequestResponse => {
          return {
            success: true,
            message: "Help request submitted successfully",
            ticketId: 12345,
          };
        },
      }),
      submitOrderCancellation: builder.mutation<
        OrderHelp.CancellationSubmitResponse,
        OrderHelp.CancellationSubmitRequest
      >({
        query: (cancellationRequest) => ({
          url: "next/order-cancellation/submit",
          credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: cancellationRequest,
        }),
      }),
      submitOrderOtherIssue: builder.mutation<OrderHelp.OtherIssueSubmitResponse, OrderHelp.OtherIssueSubmitRequest>({
        query: (otherIssueRequest) => ({
          url: "next/order-other-issue/submit",
          credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: otherIssueRequest,
        }),
      }),
    }),
  });

export const {
  useLazyGetLastActiveProductsQuery,
  useSubscribeToProductMutation,
  useSubmitOrderReturnMutation,
  useSubmitOrderHelpRequestMutation,
  useSubmitOrderCancellationMutation,
  useSubmitOrderOtherIssueMutation,
  util: { getRunningQueriesThunk },
} = orderApi;

export const {
  getLastActiveProducts,
  subscribeToProduct,
  submitOrderReturn,
  submitOrderHelpRequest,
  submitOrderCancellation,
  submitOrderOtherIssue,
} = orderApi.endpoints;
